import React, { useCallback, useEffect, useState } from 'react';

// "Do you want to leave or save?" prompt shown when a user hits Cancel / Close
// on any report editor. Esc = stay (captured so the editor-level Esc → Projects
// shortcut never fires while this is open).
export default function ConfirmLeaveDialog({
  open,
  title = 'Unsaved changes',
  message = 'Do you want to leave or save?',
  saveLabel = 'Save & Leave',
  leaveLabel = 'Leave without saving',
  stayLabel = 'Keep editing',
  onSave,
  onLeave,
  onStay,
}) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        if (onStay) onStay();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [open, onStay]);

  if (!open) return null;

  return (
    <div
      data-overlay="confirm-leave"
      style={{ position: 'fixed', inset: 0, zIndex: 20000, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onClick={onStay}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 400, padding: '22px 22px 18px', boxShadow: '0 25px 60px rgba(0,0,0,0.3)', fontFamily: "'Inter','Segoe UI',system-ui,sans-serif", textAlign: 'left' }}
      >
        <div style={{ fontSize: 17, fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>{title}</div>
        <div style={{ fontSize: 14, color: '#475569', lineHeight: 1.5, marginBottom: 18 }}>{message}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <button
            type="button"
            onClick={onSave}
            style={{ background: '#00A19D', color: '#fff', border: 'none', borderRadius: 8, padding: '11px', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}
          >
            {saveLabel}
          </button>
          <button
            type="button"
            onClick={onLeave}
            style={{ background: '#FFF1F0', color: '#DC2626', border: '1px solid #FECACA', borderRadius: 8, padding: '11px', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}
          >
            {leaveLabel}
          </button>
          <button
            type="button"
            onClick={onStay}
            style={{ background: '#F1F5F9', color: '#334155', border: 'none', borderRadius: 8, padding: '11px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
          >
            {stayLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// Convenience hook: `requestClose` replaces an editor's Cancel/X handler,
// `dialog` is rendered once anywhere inside the editor's root element.
export function useLeaveConfirm({ onSave, onLeave }) {
  const [open, setOpen] = useState(false);
  const requestClose = useCallback(() => setOpen(true), []);
  const stay = useCallback(() => setOpen(false), []);
  const dialog = (
    <ConfirmLeaveDialog
      open={open}
      onStay={stay}
      onSave={() => { setOpen(false); if (onSave) onSave(); }}
      onLeave={() => { setOpen(false); if (onLeave) onLeave(); }}
    />
  );
  return { requestClose, dialog, isOpen: open };
}
